import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import {
  XMarkIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowsRightLeftIcon,
  ShieldCheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../shared/spinner';
import { SwapResponse } from '../../api/types';
import { formatTimeRemaining } from '../../utils';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string;
  originalUrl: string | null;
  showOriginal: boolean;
  onToggleOriginal: () => void;
  mediaContentType: string;
  onDownload: () => Promise<void>;
  isDownloading: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  selectedSwap: SwapResponse | null;
}

export default function SwapPreviewModal({
   isOpen,
   onClose,
   mediaUrl,
   originalUrl,
   showOriginal,
   onToggleOriginal,
   mediaContentType,
   onDownload,
   isDownloading,
   videoRef,
   selectedSwap
 }: PreviewModalProps) {
  const [showTooltip, setShowTooltip] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [showUrgentNotice, setShowUrgentNotice] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [deletedTime, setDeletedTime] = useState<Date | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isVideo = mediaContentType.startsWith('video');

  useEffect(() => {
    if (!isOpen) {
      setShowTooltip(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const updateTimer = () => {
      if (!selectedSwap?.createdAt) {
        return;
      }

      // Check if already expired flag is set
      if (selectedSwap.targetFaceDeletedAt) {
        setTimeRemaining('Deleted');
        setIsExpired(true);
        setDeletedTime(new Date(selectedSwap.targetFaceDeletedAt));
        return;
      }

      const expiresIn = new Date(selectedSwap.targetFaceDeletesIn).getTime();
      const now = new Date().getTime();
      const remaining = expiresIn - now;

      if (remaining <= 0) {
        setTimeRemaining('Deleted');
        setIsExpired(true);
        return;
      }

      setTimeRemaining(formatTimeRemaining(remaining));
      setShowUrgentNotice(remaining < 10 * 60 * 1000); // Show urgent notice if less than 10 minutes
    };

    updateTimer();

    let timer = setInterval(updateTimer, 60000);
    return () => {
      clearInterval(timer)
    };
  }, [selectedSwap]);

  const handleMediaClick = () => {
    if (!isVideo && !isExpired) {
      onToggleOriginal();
      setShowTooltip(false);
    }
  };

  const formatDeletedDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className='relative z-50'>
      <DialogBackdrop className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />

      <div className='fixed inset-0 z-10 flex items-center justify-center p-4'>
        <DialogPanel className='relative w-full max-w-4xl rounded-lg bg-white shadow-xl'>
          {/* Close button */}
          <button
            onClick={onClose}
            className='absolute right-2 top-2 z-10 rounded-full bg-white/80 p-1.5 text-gray-500 hover:text-gray-700'
          >
            <XMarkIcon className='h-5 w-5' />
          </button>

          {/* Media preview */}
          <div className='relative flex items-center bg-black'>
            <div
              ref={containerRef}
              className={`relative w-full ${!isVideo && !isExpired && 'cursor-pointer'}`}
              onClick={handleMediaClick}
            >
              {isVideo ? (
                <div className='relative'>
                  {/* Original Video */}
                  <video
                    ref={videoRef}
                    playsInline
                    muted
                    preload='metadata'
                    webkit-playsinline='true'
                    src={originalUrl || ''}
                    controls
                    className={`mx-auto max-h-[70vh] w-auto object-contain ${!showOriginal || isExpired ? 'hidden' : ''}`}
                  />

                  {/* Anonymized Video */}
                  <video
                    playsInline
                    muted
                    preload='metadata'
                    webkit-playsinline='true'
                    src={mediaUrl}
                    controls
                    className={`mx-auto max-h-[70vh] w-auto object-contain ${showOriginal && !isExpired ? 'hidden' : ''}`}
                  />

                  {/* Video controls overlay */}
                  {!isExpired && (
                    <div className='absolute bottom-14 left-0 right-0 flex items-center justify-center pb-2'>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleOriginal();
                        }}
                        className='flex items-center gap-2 rounded-full bg-black/90 px-4 py-2 text-sm font-medium text-white'>
                        <ArrowsRightLeftIcon className='h-4 w-4' />
                        <span>{showOriginal ? 'Original' : 'Anonymized'}</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src={showOriginal && !isExpired ? originalUrl || '' : mediaUrl}
                  alt='Preview your swap'
                  className='mx-auto max-h-[70vh] w-auto object-contain'
                />
              )}

              {/* Status labels */}
              <div className='absolute left-0 top-0 z-10 p-2 sm:p-4 flex flex-col gap-2'>
                {/* Timer Badge or Deletion Confirmation */}
                {isExpired ? (
                  <div className="self-start rounded-full px-3 py-1.5 text-sm font-medium bg-green-600/90 text-white backdrop-blur-sm">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheckIcon className="h-4 w-4" />
                      Original face deleted
                    </div>
                  </div>
                ) : (
                  <div
                    className={`self-start rounded-full px-3 py-1.5 text-sm font-medium backdrop-blur-sm transition-colors
                      ${showUrgentNotice
                      ? 'bg-red-500/90 text-white'
                      : 'bg-black/70 text-white'}`}
                  >
                    <div className='flex items-center gap-1.5'>
                      {showUrgentNotice ? (
                        <ExclamationCircleIcon className='h-4 w-4' />
                      ) : (
                        <ClockIcon className='h-4 w-4' />
                      )}
                      {`Original deletes in ${timeRemaining}`}
                    </div>
                  </div>
                )}

                {/* Anonymized/Original label */}
                {!isVideo && (
                  <div className='self-start rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm sm:text-sm'>
                    {showOriginal && !isExpired ? 'Original' : 'Anonymized'}
                  </div>
                )}
              </div>

              {/* Centered tooltip - only for images */}
              {showTooltip && !isVideo && !isExpired && (
                <div
                  className='absolute text-center left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-black px-4 py-2 text-sm font-medium text-white'>
                  <span className='sm:inline hidden'>Tap image to compare</span>
                  <div className='sm:hidden'>
                    <div>Tap image to</div>
                    <div>compare</div>
                  </div>
                </div>
              )}

              {/* Deletion confirmation notice (when original is deleted) */}
              {isExpired && (
                <div className="absolute left-0 bottom-0 z-10 p-2 sm:p-4 w-full">
                  <div className="bg-black/70 text-white text-sm p-2 sm:p-3 rounded-lg backdrop-blur-sm">
                    <div className="flex items-start">
                      <TrashIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">Original face permanently deleted</p>
                        {deletedTime && (
                          <p className="text-xs text-gray-300 mt-1">Deleted on {formatDeletedDate(deletedTime)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className='space-y-2 p-3'>
            <div className='flex flex-col items-center gap-2'>
              <button
                onClick={onDownload}
                disabled={isDownloading}
                className='inline-flex items-center justify-center rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:bg-indigo-400'
              >
                {isDownloading ? (
                  <>
                    <LoadingSpinner className='mr-2 h-5 w-5' />
                    <span>Downloading</span>
                  </>
                ) : (
                  'Download'
                )}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}